import { useEffect, useRef, useState, type FormEvent, type RefObject } from "react";
import { ChevronLeft } from "lucide-react";
import MessageField from "@/components/messages/MessageField";
import MessageHtml from "@/components/messages/MessageHtml";
import { api, errorMessage, post } from "@/lib/api";
import { messagePlain, messageReady } from "@/lib/messageBody";
import type { ParentSchool } from "./ParentSchools";

type OfficeEntry = {
  id: string;
  parentId: string | null;
  body: string;
  authorName: string | null;
  authorRole: string;
  at: string;
};

type OfficeSummary = {
  id: string;
  schoolId: string;
  schoolName: string | null;
  title: string;
  students: Array<{ studentUserId: string; studentName: string }>;
  lastBody: string;
  lastAt: string;
  messageCount: number;
};

type OfficeThread = OfficeSummary & { entries: OfficeEntry[] };

function whenLabel(iso: string) {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "";
  return then.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function whoLabel(name: string | null, role: string | null) {
  if (role === "parent") return "You";
  return name ? `${name} · Office` : "Office";
}

function byTime(a: OfficeEntry, b: OfficeEntry) {
  return new Date(a.at).getTime() - new Date(b.at).getTime();
}

function childrenOf(entries: OfficeEntry[], parentId: string) {
  return entries.filter((entry) => entry.parentId === parentId).sort(byTime);
}

function Spinner() {
  return (
    <div className="parent-msg-loading" role="status">
      <span className="parent-msg-spinner" aria-hidden="true" />
      <span>Loading</span>
    </div>
  );
}

export default function ParentMessages() {
  const [rows, setRows] = useState<OfficeSummary[] | null>(null);
  const [schools, setSchools] = useState<ParentSchool[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [thread, setThread] = useState<OfficeThread | null>(null);
  const [composing, setComposing] = useState(false);
  const [schoolId, setSchoolId] = useState("");
  const [title, setTitle] = useState("");
  const [draft, setDraft] = useState("");
  const [rootReply, setRootReply] = useState("");
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [popReply, setPopReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const popRef = useRef<HTMLTextAreaElement>(null);

  const load = () => {
    api<{ success: boolean; data: OfficeSummary[] }>("/api/parent/messages")
      .then((env) => setRows(env.data || []))
      .catch((err) => setError(errorMessage(err)));
  };

  useEffect(() => {
    load();
    api<{ success: boolean; data: ParentSchool[] }>("/api/parent/schools")
      .then((env) => {
        const list = env.data || [];
        setSchools(list);
        setSchoolId(list[0]?.id || "");
      })
      .catch(() => setSchools([]));
  }, []);

  useEffect(() => {
    if (!openId) return undefined;
    let cancel = false;
    setThread(null);
    setError("");
    setReplyTo(null);
    setPopReply("");
    setRootReply("");
    api<{ success: boolean; data: OfficeThread }>(`/api/parent/messages/${openId}`)
      .then((env) => {
        if (!cancel) setThread(env.data);
      })
      .catch((err) => {
        if (!cancel) setError(errorMessage(err));
      });
    return () => {
      cancel = true;
    };
  }, [openId]);

  useEffect(() => {
    if (replyTo) popRef.current?.focus();
  }, [replyTo]);

  const sendReply = async (parentId: string, body: string) => {
    if (!openId || busy || !body.trim()) return;
    setBusy(true);
    setError("");
    try {
      const env = await post<{ success: boolean; data: OfficeThread }>(
        `/api/parent/messages/${openId}/replies`,
        { body, parentId },
      );
      setThread(env.data);
      load();
      if (parentId === env.data.id) setRootReply("");
      else {
        setReplyTo(null);
        setPopReply("");
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const sendNew = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || !schoolId || !title.trim() || !draft.trim()) return;
    setBusy(true);
    setError("");
    try {
      const env = await post<{ success: boolean; data: OfficeThread }>("/api/parent/messages", {
        schoolId,
        title,
        body: draft,
      });
      setTitle("");
      setDraft("");
      setComposing(false);
      setOpenId(env.data.id);
      load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const root = thread?.entries.find((entry) => entry.id === thread.id)
    || thread?.entries.find((entry) => !entry.parentId)
    || null;

  return (
    <div className="student-page student-page--parent">
      <div className="student-page-head">
        <span className="student-eyebrow">Messages</span>
        <h1>School office</h1>
        <p>Notes to the offices of your students’ schools. Each note is its own conversation.</p>
      </div>
      {error && !openId ? (
        <div className="student-inline-error" role="alert">
          {error}
        </div>
      ) : null}
      <div className="parent-msg-stage">
        <div className={`parent-msg-track${openId ? " is-open" : ""}`}>
          <div className="parent-msg-pane">
            {rows == null && !error ? <Spinner /> : null}
            {rows && rows.length === 0 && !composing ? <p className="student-row__sub">No messages yet.</p> : null}
            {rows && rows.length > 0 ? (
              <ul className="parent-msg-list">
                {rows.map((row) => (
                  <li key={row.id}>
                    <button type="button" className="parent-msg-open" onClick={() => setOpenId(row.id)}>
                      <span className="parent-msg-top">
                        <strong>{row.title || "Conversation"}</strong>
                        <time>{whenLabel(row.lastAt)}</time>
                      </span>
                      <span className="parent-msg-meta">
                        {[row.students.map((student) => student.studentName).join(", "), row.schoolName]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                      <span className="parent-msg-snippet">{messagePlain(row.lastBody)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            {schools.length > 0 && !composing ? (
              <button type="button" className="student-btn student-btn--sm student-btn--ghost" onClick={() => setComposing(true)}>
                New message
              </button>
            ) : null}
            {composing ? (
              <form className="parent-msg-compose" onSubmit={(event) => void sendNew(event)}>
                {schools.length > 1 ? (
                  <label htmlFor="parent-message-school">
                    School
                    <select
                      id="parent-message-school"
                      className="student-input"
                      value={schoolId}
                      onChange={(event) => setSchoolId(event.target.value)}
                    >
                      {schools.map((school) => (
                        <option key={school.id} value={school.id}>
                          {school.name || "School"}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                <label htmlFor="parent-message-title">
                  Title
                  <input
                    id="parent-message-title"
                    className="student-input"
                    value={title}
                    maxLength={200}
                    onChange={(event) => setTitle(event.target.value)}
                    placeholder="What is this about?"
                  />
                </label>
                    <MessageField
                  id="parent-message-body"
                  label="Message"
                  value={draft}
                  onChange={setDraft}
                  placeholder="Write your message"
                  disabled={busy}
                />
                <div className="parent-msg-actions">
                  <button type="button" className="student-text-link" onClick={() => setComposing(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="student-btn student-btn--sm" disabled={busy || !title.trim() || !messageReady(draft)}>
                    {busy ? "Sending…" : "Send"}
                  </button>
                </div>
              </form>
            ) : null}
            {schools.length === 0 && rows && rows.length === 0 ? (
              <p className="student-row__sub">There is no school office to contact until a student is linked.</p>
            ) : null}
          </div>
          <div className="parent-msg-pane">
            <div className="parent-msg-thread">
              <button type="button" className="parent-msg-back" onClick={() => setOpenId(null)} aria-label="Back">
                <ChevronLeft size={18} aria-hidden />
              </button>
              {error && openId ? (
                <div className="student-inline-error" role="alert">
                  {error}
                </div>
              ) : null}
              {openId && !thread && !error ? <Spinner /> : null}
              {thread && root ? (
                <>
                  <article className="parent-msg parent-msg--you">
                    <span className="parent-msg-top">
                      <strong>{thread.title || "Conversation"}</strong>
                      <time>{whenLabel(root.at)}</time>
                    </span>
                    <span className="parent-msg-meta">
                      {[
                        "You",
                        thread.students.map((student) => student.studentName).join(", "),
                        thread.schoolName,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                    <MessageHtml body={root.body} />
                  </article>
                  <div className="parent-msg-timeline">
                    {childrenOf(thread.entries, root.id).map((entry) => (
                      <ParentTimeline
                        key={entry.id}
                        entry={entry}
                        entries={thread.entries}
                        replyTo={replyTo}
                        popReply={popReply}
                        popRef={popRef}
                        busy={busy}
                        onOpen={(id) => {
                          setReplyTo((current) => (current === id ? null : id));
                          setPopReply("");
                        }}
                        onPopReply={setPopReply}
                        onSend={(parentId) => void sendReply(parentId, popReply)}
                      />
                    ))}
                  </div>
                  <form
                    className="parent-msg-reply"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void sendReply(root.id, rootReply);
                    }}
                  >
                    <MessageField
                      id="parent-message-reply"
                      label="Reply"
                      value={rootReply}
                      onChange={setRootReply}
                      placeholder="Reply to the first message"
                      disabled={busy}
                    />
                    <button type="submit" className="student-btn student-btn--sm" disabled={busy || !messageReady(rootReply)}>
                      {busy ? "Sending…" : "Send reply"}
                    </button>
                  </form>
                </>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ParentTimeline({
  entry,
  entries,
  replyTo,
  popReply,
  popRef,
  busy,
  onOpen,
  onPopReply,
  onSend,
}: {
  entry: OfficeEntry;
  entries: OfficeEntry[];
  replyTo: string | null;
  popReply: string;
  popRef: RefObject<HTMLTextAreaElement>;
  busy: boolean;
  onOpen: (id: string) => void;
  onPopReply: (value: string) => void;
  onSend: (parentId: string) => void;
}) {
  const open = replyTo === entry.id;
  const mine = entry.authorRole === "parent";
  const nested = childrenOf(entries, entry.id);
  return (
    <article className={`parent-msg${mine ? " parent-msg--you" : " parent-msg--office"}${open ? " is-open" : ""}`}>
      <button
        type="button"
        className="parent-msg-hit"
        aria-expanded={open}
        aria-label={`Reply to ${whoLabel(entry.authorName, entry.authorRole)}`}
        onClick={() => onOpen(entry.id)}
      >
        <span className="parent-msg-top">
          <strong>{whoLabel(entry.authorName, entry.authorRole)}</strong>
          <time>{whenLabel(entry.at)}</time>
        </span>
        <MessageHtml body={entry.body} />
      </button>
      {open ? (
        <form
          className="parent-msg-pop"
          onSubmit={(event) => {
            event.preventDefault();
            onSend(entry.id);
          }}
        >
          <MessageField
            id={`parent-pop-${entry.id}`}
            label="Reply to this message"
            value={popReply}
            onChange={onPopReply}
            placeholder="Write a reply"
            disabled={busy}
            textareaRef={popRef}
          />
          <button type="submit" className="student-btn student-btn--sm" disabled={busy || !messageReady(popReply)}>
            {busy ? "Sending…" : "Reply here"}
          </button>
        </form>
      ) : null}
      {nested.length > 0 ? (
        <div className="parent-msg-nest">
          {nested.map((child) => (
            <ParentTimeline
              key={child.id}
              entry={child}
              entries={entries}
              replyTo={replyTo}
              popReply={popReply}
              popRef={popRef}
              busy={busy}
              onOpen={onOpen}
              onPopReply={onPopReply}
              onSend={onSend}
            />
          ))}
        </div>
      ) : null}
    </article>
  );
}
